import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FileUpload } from '../components/FileUpload';
import { BankStatementExtractRequestForm, BankStatementExtractRequestFormRef } from '../components/BankStatementExtractRequestForm';
import { BankStatementExtractResults } from '../components/BankStatementExtractResults';
import { bankStatementExtract, ping, ApiError, generateUploadUrl, uploadFileWithUrl } from '../api';
import { BankStatementExtractRequest, BankStatementExtractResponse } from '../types';
import { config } from '../config';

enum ProcessingStep {
  FILE_SELECTION = 'file_selection',
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error'
}

const CACHE_KEY = 'formx-bank-statement-form-data';

interface FormDataWithAuth extends Omit<BankStatementExtractRequest, 'asset_id'> {
  auth_token: string;
  custom_section_enabled: boolean;
  custom_section_title: string;
  custom_section_description: string;
}

export function BankStatementExtract() {
  // Load cached form data
  const loadCachedData = (): FormDataWithAuth => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedData = JSON.parse(cached);
        return {
          ...config.defaultBankStatementFormValues,
          ...parsedData,
          // Ensure required arrays have at least one entry
          account_type_definitions: parsedData.account_type_definitions?.length > 0 
            ? parsedData.account_type_definitions 
            : [{ name: '' }],
          field_definitions: parsedData.field_definitions?.length > 0 
            ? parsedData.field_definitions 
            : [{ name: '' }],
          auth_token: parsedData.auth_token || ''
        };
      }
    } catch (error) {
      console.warn('Failed to load cached form data:', error);
    }
    
    return {
      auth_token: '',
      llm_model_provider: config.defaultBankStatementFormValues.llmModelProvider,
      llm_model_name: null,
      chat_complete_request_args: null,
      openrouter_api_key: null,
      include_text: config.defaultBankStatementFormValues.includeText,
      include_image: config.defaultBankStatementFormValues.includeImage,
      endpoint: '',
      token: '',
      workspace_or_extractor_id: '',
      is_workspace: config.defaultBankStatementFormValues.isWorkspace,
      pdf_dpi: config.defaultBankStatementFormValues.pdfDpi,
      auto_adjust_image_size: config.defaultBankStatementFormValues.autoAdjustImageSize,
      original_file_name: null,
      account_type_definitions: [],
      field_definitions: [{ name: '' }],
      custom_section: null,
      batch_size: config.defaultBankStatementFormValues.batchSize,
      add_prompt: config.defaultBankStatementFormValues.addPrompt,
      custom_section_enabled: false,
      custom_section_title: '',
      custom_section_description: ''
    };
  };

  const [step, setStep] = useState<ProcessingStep>(ProcessingStep.FILE_SELECTION);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSucceeded, setUploadSucceeded] = useState<boolean>(false);
  const [assetId, setAssetId] = useState<string>('');
  const [results, setResults] = useState<BankStatementExtractResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataWithAuth>(loadCachedData);
  const [isConfigCollapsed, setIsConfigCollapsed] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const formRef = useRef<BankStatementExtractRequestFormRef>(null);

  // Validation utility function for fallback when form is not mounted
  const validateFormData = useCallback((data: FormDataWithAuth): boolean => {
    const baseValid = (
      data.endpoint.trim() !== '' &&
      data.token.trim() !== '' &&
      data.workspace_or_extractor_id.trim() !== '' &&
      data.account_type_definitions.some(def => def.name.trim() !== '') &&
      data.field_definitions.some(def => def.name.trim() !== '')
    );

    if (data.custom_section_enabled) {
      return (
        baseValid &&
        data.custom_section_title.trim() !== '' &&
        data.custom_section_description.trim() !== ''
      );
    }

    return baseValid;
  }, []);

  // Initialize form validity based on cached data
  useEffect(() => {
    setIsFormValid(validateFormData(loadCachedData()));
  }, [validateFormData]);

  // Cache form data whenever it changes
  const handleFormDataChange = (newFormData: FormDataWithAuth) => {
    setFormData(newFormData);
    // Check form validity using ref if form is mounted, otherwise use callback validation
    const isValid = formRef.current ? formRef.current.isValid() : validateFormData(newFormData);
    setIsFormValid(isValid);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(newFormData));
    } catch (error) {
      console.warn('Failed to cache form data:', error);
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    try {
      await ping();
      setIsConnected(true);
      toast.success('Successfully connected to backend');
    } catch (err) {
      setIsConnected(false);
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to connect to backend';
      toast.error(errorMessage);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    setUploadSucceeded(false);
    setAssetId('');
    setError('');
    setStep(ProcessingStep.UPLOADING);

    try {
      const uploadUrlResponse = await generateUploadUrl(
        formData.auth_token,
        file.size,
        file.type
      );
      await uploadFileWithUrl(uploadUrlResponse, file);
      setAssetId(uploadUrlResponse.asset_id);
      setUploadSucceeded(true);
      setStep(ProcessingStep.FILE_SELECTION);
      toast.success('File uploaded successfully');
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to upload file';
      setError(errorMessage);
      setStep(ProcessingStep.ERROR);
      toast.error(errorMessage);
      setSelectedFile(null);
    }
  };

  const handleFormSubmit = async (formData: Omit<BankStatementExtractRequest, 'asset_id'>, formAuthToken: string) => {
    if (!assetId) {
      const errorMessage = 'No file uploaded';
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setError('');
    setResults(null);
    setStep(ProcessingStep.PROCESSING);

    try {
      const request: BankStatementExtractRequest = {
        ...formData,
        asset_id: assetId,
        original_file_name: selectedFile?.name || null,
      };

      const response = await bankStatementExtract(request, formAuthToken);
      setResults(response);
      setStep(ProcessingStep.COMPLETED);
      toast.success('Bank statement processed successfully');
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message}${err.detail ? `: ${JSON.stringify(err.detail)}` : ''}` 
        : 'Processing failed';
      setError(errorMessage);
      setStep(ProcessingStep.ERROR);
      toast.error(errorMessage);
    }
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadSucceeded(false);
    setAssetId('');
    setResults(null);
    setError('');
    setStep(ProcessingStep.FILE_SELECTION);
  };

  const isProcessing = step === ProcessingStep.UPLOADING || step === ProcessingStep.PROCESSING;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>FormX Bank Statement Extract</h1>
          {/* FIXME: refactor the navigation links implementation to make it scalable to future items */}
          <nav className="app-nav" aria-label="Main">
            <Link to="/bank-statement-extract" className="btn btn-nav active" aria-current="page">
              Bank Statement Extract
            </Link>
            <Link to="/split-and-classify" className="btn btn-nav" target="_blank" rel="noopener noreferrer">
              Split & Classify
            </Link>
          </nav>
        </div>
        <div className="connection-status">
          {isConnected === null && (
            <button 
              onClick={handleCheckConnection} 
              className="btn btn-secondary"
              disabled={isCheckingConnection}
            >
              {isCheckingConnection ? 'Checking...' : 'Check Connection'}
            </button>
          )}
          {isConnected === true && (
            <>
              <span className="status-connected">✓ Connected</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Recheck'}
              </button>
            </>
          )}
          {isConnected === false && (
            <>
              <span className="status-disconnected">✗ Backend unavailable</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Retry Connection'}
              </button>
            </>
          )}
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <div className="left-panel">
            <div className="upload-section">
              <h2>Upload PDF File</h2>
              {selectedFile && uploadSucceeded ? (
                step === ProcessingStep.PROCESSING ? (
                  <div className="uploading-state">
                    <div className="upload-progress">
                      <span>Processing {selectedFile?.name}...</span>
                    </div>
                    <div className="upload-details">
                      Size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : '0'} MB
                    </div>
                  </div>
                ) : (
                  <div className="file-selected">
                    <span className="file-info">
                      ✓ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button onClick={handleReset} className="btn btn-secondary">
                      Choose Different File
                    </button>
                  </div>
                )
              ) : step === ProcessingStep.UPLOADING ? (
                <div className="uploading-state">
                  <div className="upload-progress">
                    <div className="loading-spinner"></div>
                    <span>Uploading {selectedFile?.name}...</span>
                  </div>
                  <div className="upload-details">
                    Size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : '0'} MB
                  </div>
                </div>
              ) : (
                <FileUpload onFileSelect={handleFileSelect} disabled={isProcessing} />
              )}
              
              {/* Submit Button */}
              {uploadSucceeded && (
                <div className="submit-section">
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    disabled={!isFormValid || isProcessing}
                    onClick={() => {
                      const { auth_token, custom_section_enabled, custom_section_title, custom_section_description, ...requestData } = formData;
                      const customSection = custom_section_enabled && custom_section_title.trim() && custom_section_description.trim()
                        ? `# ${custom_section_title.trim()}\n\n${custom_section_description.trim()}`
                        : null;

                      const payload = {
                        ...requestData,
                        custom_section: customSection,
                        chat_complete_request_args: formData.chat_complete_request_args,
                      };
                      handleFormSubmit(payload, auth_token);
                    }}
                  >
                    Extract Transactions
                  </button>
                </div>
              )}
            </div>

            <div className="config-section">
              <div 
                className="config-header" 
                onClick={() => setIsConfigCollapsed(!isConfigCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsConfigCollapsed(!isConfigCollapsed);
                  }
                }}
                aria-expanded={!isConfigCollapsed}
                aria-controls="config-form"
              >
                <button 
                  type="button" 
                  className={`collapse-toggle ${isConfigCollapsed ? 'collapsed' : ''}`}
                  aria-label={isConfigCollapsed ? 'Expand configuration' : 'Collapse configuration'}
                >
                  <span>▼</span>
                </button>
                <h2>Configure Extraction</h2>
              </div>
              {!isConfigCollapsed && (
                <div id="config-form">
                  <BankStatementExtractRequestForm 
                    ref={formRef}
                    formData={formData}
                    onFormDataChange={handleFormDataChange}
                    onSubmit={(requestData, authToken) => handleFormSubmit(requestData, authToken)}
                    disabled={isProcessing}
                    onValidationChange={handleValidationChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="right-panel">
            {step === ProcessingStep.UPLOADING && (
              <div className="status-message">
                <h3>Uploading file...</h3>
                <div className="loading-spinner"></div>
              </div>
            )}

            {step === ProcessingStep.PROCESSING && (
              <div className="status-message">
                <h3>Processing bank statement...</h3>
                <p>This may take a few minutes depending on document size and complexity.</p>
                <div className="loading-spinner"></div>
              </div>
            )}

            {step === ProcessingStep.ERROR && (
              <div className="error-message">
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            )}

            {step === ProcessingStep.COMPLETED && results && (
              <div className="results-container">
                <BankStatementExtractResults results={results} onDownload={handleDownload} />
              </div>
            )}

            {selectedFile && !results && step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>{uploadSucceeded ? 'Ready to extract' : 'File selected, upload in progress or failed'}</h3>
                <p>{uploadSucceeded ? 'Configure your settings and click "Extract Transactions" to begin.' : 'Please wait for upload to complete or try uploading again.'}</p>
              </div>
            )}

            {!selectedFile && step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>Upload a PDF to get started</h3>
                <p>You can configure your settings now, but you'll need to upload a PDF file before processing.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
